// apps/cli/src/lib/prereqs.ts
//
// THE source of truth for "what has to be true for m8t to install, and for a
// person to actually use it".
//
// Two phases, because they are answerable at different moments:
//
//   install — answerable BEFORE anything exists. Providers, quota, the caller's
//             subscription + directory rights.
//   usage   — answerable only AFTER the platform exists, because the grants are
//             scoped to resources the installer creates (the Foundry account is
//             named from the resource group INSIDE the ACI; the Key Vault comes
//             out of `m8t deploy`). A role assignment cannot target a resource
//             that does not exist yet — which is why the founder's grants land
//             once the platform exists (the cloud installer's step 3b, with
//             `bootstrap status` as the local backstop) and not at preflight.
//
// The exported PREREQS array is the map. It carries rows this module does NOT
// itself check (`checkedBy: "doctor" | "runbook"`) and rows nothing checks at all
// (`coverage: "unhandled"`) ON PURPOSE: an honest absence beats a silent one, and
// the docs table is pinned to this array by a drift test. Slugs are stable and
// enum-safe so they can be adopted as a shared failure vocabulary.
//
// Posture (do not re-derive): REFUSE only on stable, deterministically-observed
// blockers. Transient or unverifiable → warn and proceed; the install now fails
// honestly downstream, so a founder blocked by a network blip is the worse
// outcome. A probe that cannot run at all → "skipped", never silence.

export type PrereqPhase = "install" | "usage";

/** Four-valued on purpose: a finding and an absence of evidence are different sentences. */
export type PrereqStatus = "pass" | "fail" | "warn" | "skipped" | "fixed";

/** How the prerequisite is satisfied today. "unhandled" rows are real and stay listed. */
export type PrereqCoverage = "auto" | "refuses" | "warns" | "unhandled";

/** Which surface actually evaluates the row. Keeps `m8t prereqs` and `m8t doctor` single-purpose. */
export type PrereqCheckedBy = "prereqs" | "doctor" | "runbook" | "none";

export interface PrereqSpec {
  /** Stable, enum-safe identifier. Never rename — downstream vocabularies adopt these. */
  slug: string;
  phase: PrereqPhase;
  /** Founder-facing one-liner. */
  title: string;
  /** What breaks without it, in the founder's terms. */
  breaks: string;
  coverage: PrereqCoverage;
  checkedBy: PrereqCheckedBy;
  /** True when `m8t prereqs --fix` can repair it unattended. */
  fixable: boolean;
}

export interface PrereqResult {
  slug: string;
  phase: PrereqPhase;
  title: string;
  status: PrereqStatus;
  detail: string;
  /** A command THIS caller can run on THIS surface. Present on every fail. */
  remedy?: string;
}

// ─── The map ────────────────────────────────────────────────────────────────
//
// Order is the order the docs table renders in.

export const PREREQS: readonly PrereqSpec[] = [
  // ── install phase ────────────────────────────────────────────────────────
  {
    slug: "local-tooling",
    phase: "install",
    title: "git, Azure CLI, Node 20+, and the m8t CLI on your machine",
    breaks: "Nothing can run at all.",
    coverage: "auto",
    checkedBy: "runbook",
    fixable: false,
  },
  {
    slug: "az-signed-in",
    phase: "install",
    title: "Signed in to Azure with an active subscription",
    breaks: "Every Azure call fails immediately.",
    coverage: "refuses",
    checkedBy: "prereqs",
    fixable: false,
  },
  {
    slug: "subscription-admin",
    phase: "install",
    title: "Owner or User Access Administrator at subscription scope",
    breaks: "The install cannot create its managed identity or assign it roles.",
    coverage: "refuses",
    checkedBy: "prereqs",
    fixable: false,
  },
  {
    slug: "app-registration",
    phase: "install",
    title: "Directory rights to register one Entra app (or a ready --client-id)",
    breaks: "There is no app registration, so nobody can ever sign in to the webapp.",
    coverage: "refuses",
    checkedBy: "prereqs",
    fixable: false,
  },
  {
    slug: "resource-providers",
    phase: "install",
    title: "Every Azure resource provider the install uses is registered",
    breaks:
      "The installer dies partway through creating resources, with an error that names a provider rather than anything actionable.",
    coverage: "refuses",
    checkedBy: "prereqs",
    fixable: true,
  },
  {
    slug: "model-quota",
    phase: "install",
    title: "Model quota for the reasoning model in the target region",
    breaks: "The install spends money creating resources and then cannot deploy the model the workers need.",
    coverage: "refuses",
    checkedBy: "prereqs",
    fixable: false,
  },
  {
    slug: "github-org",
    phase: "install",
    title: "A GitHub organization and the rights to install an App on it",
    breaks: "The brain-backed workers have nowhere to keep their memory.",
    coverage: "refuses",
    checkedBy: "runbook",
    fixable: false,
  },
  {
    slug: "azure-policy",
    phase: "install",
    title: "No Azure Policy or deny assignment blocking resource creation",
    breaks: "The installer is refused by governance partway through, with a policy error.",
    coverage: "unhandled",
    checkedBy: "none",
    fixable: false,
  },
  {
    slug: "region-eligibility",
    phase: "install",
    title: "The target region can host every service the platform uses",
    breaks: "A resource fails to create in that region partway through the install.",
    coverage: "unhandled",
    checkedBy: "none",
    fixable: false,
  },
  {
    slug: "soft-deleted-account",
    phase: "install",
    title: "No soft-deleted Cognitive Services account still holding the model quota",
    breaks: "Quota looks free but the model deployment fails; the quota is held by an account you already deleted.",
    coverage: "refuses",
    checkedBy: "prereqs",
    // Reported, never repaired. Purging is irreversible and the account may be
    // one the founder means to recover — `--fix` names the command instead.
    fixable: false,
  },
  {
    slug: "subscription-billing",
    phase: "install",
    title: "The subscription is active and can be billed",
    breaks: "Resource creation is refused for billing reasons.",
    coverage: "unhandled",
    checkedBy: "none",
    fixable: false,
  },

  // ── usage phase ──────────────────────────────────────────────────────────
  {
    slug: "platform-discoverable",
    phase: "usage",
    title: "The m8t CLI can find the platform you are pointed at",
    breaks: "Every CLI command that talks to the platform fails to find it.",
    coverage: "refuses",
    checkedBy: "prereqs",
    fixable: false,
  },
  {
    slug: "signin-redirect-uri",
    phase: "usage",
    title: "The webapp's sign-in redirect URI is registered on the app",
    breaks: "Sign-in fails with AADSTS50011 on a platform that is otherwise perfectly healthy.",
    coverage: "auto",
    checkedBy: "prereqs",
    // Reported, not repaired. The app registration is shared across every
    // deployment in the tenant; writing it belongs to the install.
    fixable: false,
  },
  {
    slug: "foundry-data-plane",
    phase: "usage",
    title: "Your account can reach the Foundry data plane",
    breaks:
      "Your AI team will not load, chat does not work, and the CLI and coding-agent plugin cannot reach your workers. Subscription Owner does NOT cover this — it is a data action, and control-plane roles carry none.",
    coverage: "auto",
    checkedBy: "prereqs",
    fixable: true,
  },
  {
    slug: "keyvault-secrets",
    phase: "usage",
    title: "Your account can read the platform's Key Vault secrets",
    breaks: "Deploying a worker fails — the coding agent and the Azure executor both read the vault as you.",
    coverage: "auto",
    checkedBy: "prereqs",
    fixable: true,
  },
  {
    slug: "local-state",
    phase: "usage",
    title: "Local state the coding-agent plugin needs (~/.m8t/repo-root, config)",
    breaks: "The m8t plugin cannot list or reach your workers from an agent session.",
    coverage: "warns",
    checkedBy: "doctor",
    fixable: false,
  },
];

export function prereqsForPhase(phase: PrereqPhase): readonly PrereqSpec[] {
  return PREREQS.filter((p) => p.phase === phase);
}

export function prereqBySlug(slug: string): PrereqSpec | undefined {
  return PREREQS.find((p) => p.slug === slug);
}

function spec(slug: string): PrereqSpec {
  const s = prereqBySlug(slug);
  if (!s) throw new Error(`unknown prereq slug: ${slug}`);
  return s;
}

function result(slug: string, status: PrereqStatus, detail: string, remedy?: string): PrereqResult {
  const s = spec(slug);
  return { slug: s.slug, phase: s.phase, title: s.title, status, detail, ...(remedy ? { remedy } : {}) };
}

// ─── Install-phase evaluators (pure) ────────────────────────────────────────

/**
 * Every resource provider the install creates something under. Microsoft.Authorization
 * is deliberately absent — role assignments need no provider registration.
 */
export const REQUIRED_PROVIDERS = [
  "Microsoft.ContainerInstance", // the installer job itself
  "Microsoft.ManagedIdentity", // installer MI + gateway user-assigned identity
  "Microsoft.CognitiveServices", // Foundry account, project, model deployments
  "Microsoft.App", // Container Apps environment, gateway, updater job
  "Microsoft.OperationalInsights", // Log Analytics workspace
  "Microsoft.Storage", // status blob + platform tables/blobs
  "Microsoft.KeyVault", // platform secrets
  "Microsoft.ServiceBus", // gateway queues
  "Microsoft.Insights", // Application Insights
  "Microsoft.ContainerRegistry", // hosted-agent image staging
] as const;

export interface ProviderState {
  namespace: string;
  /** Azure's registrationState verbatim, or null when the read failed. */
  state: string | null;
}

/**
 * Refuse only on a namespace we could READ and that is not registered — that is
 * stable and deterministic. A namespace we could not read at all is unverifiable,
 * so it warns. Registering is in-progress → warn, not refuse: it resolves itself.
 */
export function evaluateProviders(states: readonly ProviderState[]): PrereqResult {
  // Zero results is not "everything is registered" — it means the probe did not
  // run. Reporting a pass here would green-light an install having verified
  // nothing, which is the exact failure this whole check exists to prevent.
  if (states.length === 0) {
    return result("resource-providers", "skipped", "no provider registration state could be read — proceeding unverified");
  }
  const unreadable = states.filter((s) => s.state === null).map((s) => s.namespace);
  const unregistered = states
    .filter((s) => s.state !== null && s.state.toLowerCase() !== "registered" && s.state.toLowerCase() !== "registering")
    .map((s) => s.namespace);
  const registering = states.filter((s) => s.state?.toLowerCase() === "registering").map((s) => s.namespace);

  if (unregistered.length > 0) {
    return result(
      "resource-providers",
      "fail",
      `not registered on this subscription: ${unregistered.join(", ")}`,
      unregistered.map((ns) => `az provider register --namespace ${ns}`).join("\n        "),
    );
  }
  if (registering.length > 0) {
    return result("resource-providers", "warn", `still registering: ${registering.join(", ")} — this resolves on its own, usually within a minute`);
  }
  if (unreadable.length > 0) {
    return result("resource-providers", "skipped", `could not read registration state for: ${unreadable.join(", ")} — proceeding unverified`);
  }
  return result("resource-providers", "pass", `all ${states.length.toString()} required providers registered`);
}

// ─── Soft-deleted Cognitive Services accounts ───────────────────────────────
//
// A soft-deleted account keeps its model quota until it is PURGED. That is why
// this row exists at all: `model-quota` can read perfectly clear while a deleted
// account holds the capacity, so a founder passes preflight and then fails at
// model deployment — a false green, not a missing check.
//
// What is observable and what is not, kept strictly apart:
//   observable     — that a soft-deleted account exists, in which region.
//   NOT observable — whether it currently holds quota. A deleted account's
//                    deployments cannot be listed.
// So the copy states the MECHANISM and the CONSEQUENCE, never a measurement it
// did not make. Refusing on existence is deliberate (a founder retrying after
// deleting a previous install is the dominant case), and the remedy names every
// exit — purge, recover, or another region.

export interface SoftDeletedAccount {
  name: string;
  /** Azure's location, however it is spelled ("eastus2" or "East US 2"). */
  location: string;
  /** Azure's account kind — decides whether it can hold model quota at all. */
  kind: string;
  /** Parsed from the ARM id. null when the id carried none — never invented. */
  resourceGroup: string | null;
}

/**
 * The account kinds that can carry MODEL deployments, and therefore model quota.
 *
 * `list-deleted` returns every soft-deleted Cognitive Services account in the
 * subscription, of every kind — SpeechServices, ComputerVision, TextTranslation
 * and the rest included. None of those hold a single unit of model quota, so
 * refusing an install over one would block a founder on an irrelevant resource
 * AND point an irreversible purge command at something that is very likely
 * someone else's. Kinds outside this set are read, and reported, but never
 * refused on.
 */
const MODEL_HOSTING_KINDS = new Set(["aiservices", "openai", "cognitiveservices"]);

function holdsModelQuota(a: SoftDeletedAccount): boolean {
  return MODEL_HOSTING_KINDS.has(a.kind.trim().toLowerCase());
}

export interface SoftDeletedEvaluation {
  /** False when the ARM read failed; `reason` then says why. */
  readable: boolean;
  accounts: readonly SoftDeletedAccount[];
  /** The az failure, surfaced verbatim. Absent when the read succeeded. */
  reason?: string;
  /** The region the install targets. Absent when no region has been chosen yet. */
  region?: string;
}

/** "East US 2", "eastus2" and "EastUS2" are one region. */
export function sameRegion(a: string, b: string): boolean {
  const norm = (s: string) => s.replace(/\s+/g, "").toLowerCase();
  return norm(a) === norm(b);
}

/**
 * Purge + recover for every account, with the name, group and location captured
 * from the read. Never a pattern: a purge is irreversible, and a wildcard would
 * invite reaping an account this command never looked at.
 */
function softDeletedRemedy(accounts: readonly SoftDeletedAccount[]): string {
  const purge = accounts
    .map((a) => `az cognitiveservices account purge -n ${a.name} -g ${a.resourceGroup ?? "<resource-group-it-was-in>"} -l ${a.location}`)
    .join("\n        ");
  const it = accounts.length === 1 ? "it" : "them";
  return (
    `Purge ${it} — permanent, the account and its model deployments cannot be recovered afterwards:\n` +
    `        ${purge}\n` +
    `        Meant to keep one? 'az cognitiveservices account recover' takes the same -n/-g/-l and restores it (its quota then reads honestly again).\n` +
    `        Or install into a region with no soft-deleted account.`
  );
}

/** "…(kind SpeechServices) holds no model quota" — named so the founder can see what was set aside. */
function asideNote(others: readonly SoftDeletedAccount[], where: string): string {
  if (others.length === 0) return "";
  const kinds = [...new Set(others.map((a) => a.kind || "unknown kind"))].join(", ");
  return ` (${others.length.toString()} other soft-deleted ${others.length === 1 ? "account" : "accounts"} ${where} — ${kinds} — ${others.length === 1 ? "cannot hold" : "cannot hold"} model quota, so ${others.length === 1 ? "it is" : "they are"} not judged here)`;
}

/**
 * Four cells, because the region decides how the read is judged — never whether
 * it happens. Plain `m8t prereqs` is often the first command a founder runs,
 * before any region is chosen, and the warn is the one cheap signal that can
 * still shape that choice.
 */
export function evaluateSoftDeletedAccounts(e: SoftDeletedEvaluation): PrereqResult {
  if (!e.readable) {
    // Absence of evidence, reported WITH its cause. A skip whose reason was
    // swallowed leaves a founder unable to tell a permissions gap from a blip.
    return result(
      "soft-deleted-account",
      "skipped",
      `could not list this subscription's soft-deleted Cognitive Services accounts — proceeding unverified (${e.reason ?? "no reason reported"})`,
    );
  }

  const mechanism = "A soft-deleted account keeps its model quota until it is purged";
  // Only model-hosting kinds are judged; the rest are named but never refused on.
  const quotaBearing = e.accounts.filter(holdsModelQuota);
  const others = e.accounts.filter((a) => !holdsModelQuota(a));

  if (e.region) {
    const region = e.region;
    const here = quotaBearing.filter((a) => sameRegion(a.location, region));
    if (here.length === 0) {
      return result(
        "soft-deleted-account",
        "pass",
        `no soft-deleted model-hosting accounts in ${region}` +
          asideNote(others.filter((a) => sameRegion(a.location, region)), "there"),
      );
    }
    const noun = here.length === 1 ? "account" : "accounts";
    return result(
      "soft-deleted-account",
      "fail",
      `${here.length.toString()} soft-deleted Cognitive Services ${noun} in ${region}: ` +
        `${here.map((a) => `${a.name} (${a.kind || "unknown kind"})`).join(", ")}. ` +
        `${mechanism}, so quota can read free here and the model deployment still fail.`,
      softDeletedRemedy(here),
    );
  }

  if (quotaBearing.length === 0) {
    return result(
      "soft-deleted-account",
      "pass",
      "no soft-deleted model-hosting accounts anywhere in this subscription" + asideNote(others, "in this subscription"),
    );
  }
  const regions = [...new Set(quotaBearing.map((a) => a.location.replace(/\s+/g, "").toLowerCase()))];
  const noun = quotaBearing.length === 1 ? "account" : "accounts";
  return result(
    "soft-deleted-account",
    "warn",
    `no region given, so this cannot be judged against your target. This subscription has ` +
      `${quotaBearing.length.toString()} soft-deleted Cognitive Services ${noun}: ` +
      `${quotaBearing.map((a) => `${a.name} (${a.location})`).join(", ")}. ` +
      `${mechanism} — installing into ${regions.length === 1 ? regions[0] : "one of those regions"} would let quota read free while the model deployment still fails.`,
    softDeletedRemedy(quotaBearing),
  );
}

export interface QuotaEvaluation {
  /** From modelQuotaVerdict(). */
  verdict: "ok" | "no_quota" | "unknown";
  /** Reasoning-family models that DO have quota in the region, for the remedy line. */
  quotad: readonly string[];
  model: string;
  region: string;
  /** False when `az cognitiveservices usage list` returned nothing at all. */
  usagesReadable: boolean;
}

// A note on why this row carries NO soft-deleted cross-reference, measured live
// on 2026-07-28 rather than reasoned about:
//
//   gpt-5.4 GlobalStandard, westus3     currentValue   limit
//   baseline                                   1500   30000
//   after deploying 1000 units                 2500   30000
//   after SOFT-DELETING that account           2500   30000   ← quota still held
//   after PURGING it                           1500   30000   ← released
//
// The trap raises `currentValue`; it never touches `limit`. `modelQuotaVerdict`
// judges `limit > 0` alone, so a soft-deleted account can NEVER produce the
// `no_quota` verdict — it produces `ok`. An earlier draft appended "this
// shortfall may be held by <account>" to the zero-quota refusal, which reads
// plausibly and is wrong in every case it could print: zero quota is a real cap,
// not a hold. The refusal a founder needs comes from the soft-deleted-account row
// itself, which fails independently and prints the purge command.
//
// The honest cross-reference belongs on a near-exhausted PASS (currentValue at
// limit, limit > 0) — and that needs `currentValue`, which nothing reads today.
// That is a separate, larger gap: a region whose capacity is fully consumed by
// LIVE accounts also reads pass. Deliberately not fixed here.

/**
 * Explicit zero quota is the only refusal: matching usage entries exist and every
 * one has limit 0. No matching entry ("unknown") means we cannot tell — that warns.
 * An unreadable usage list is an absence of evidence, so it is "skipped".
 */
export function evaluateModelQuota(e: QuotaEvaluation): PrereqResult {
  if (!e.usagesReadable) {
    return result("model-quota", "skipped", `could not read model quota in ${e.region} — proceeding unverified`);
  }
  if (e.verdict === "no_quota") {
    const alt = e.quotad.length > 0 ? ` Models with quota there: ${e.quotad.join(", ")}.` : "";
    return result(
      "model-quota",
      "fail",
      `${e.model} has zero quota in ${e.region}.${alt}`,
      `Request quota for ${e.model} in ${e.region} in the Azure portal (Foundry → Quotas), or install into a region that already has it.`,
    );
  }
  if (e.verdict === "unknown") {
    return result("model-quota", "warn", `no quota entry for ${e.model} in ${e.region} — cannot confirm; the install will report honestly if it is missing`);
  }
  return result("model-quota", "pass", `${e.model} has quota in ${e.region}`);
}

export function evaluateAzSignedIn(account: { upn: string; tenantId: string; subscriptionId: string } | null): PrereqResult {
  if (!account) return result("az-signed-in", "fail", "not signed in to Azure", "az login");
  return result("az-signed-in", "pass", `${account.upn} (tenant ${account.tenantId}, subscription ${account.subscriptionId})`);
}

export function evaluateSubscriptionAdmin(probe: { ok: boolean; role?: string }): PrereqResult {
  if (!probe.ok) {
    return result(
      "subscription-admin",
      "fail",
      "you are not Owner or User Access Administrator at subscription scope",
      "Ask a subscription Owner to run the install — creating and granting the installer's managed identity is unavoidable.",
    );
  }
  return result("subscription-admin", "pass", `${probe.role ?? "Owner/User Access Administrator"} at subscription scope`);
}

export function evaluateAppRegistration(probe: { ok: boolean; inconclusive: boolean }, clientId?: string): PrereqResult {
  if (clientId) return result("app-registration", "pass", `using your app registration ${clientId}`);
  if (probe.inconclusive) {
    return result(
      "app-registration",
      "fail",
      "could not verify your directory admin role (Microsoft Graph denied the read — common for guest accounts)",
      "Re-run with --client-id <appId> using an app registration an admin prepared for you.",
    );
  }
  if (!probe.ok) {
    return result(
      "app-registration",
      "fail",
      "you do not hold a directory admin role (Application / Cloud Application / Global Administrator)",
      "Re-run with --client-id <appId> using an app registration an admin prepared for you.",
    );
  }
  return result("app-registration", "pass", "you can register the m8t app in this directory");
}

// ─── Usage-phase evaluators (pure) ──────────────────────────────────────────

/**
 * How we learned whether the principal has access.
 *  - "probe":      we called the data plane AS them and read the real answer. Authoritative.
 *  - "assignment": we inspected role assignments. Used for `--for <someone else>`, where
 *                  probing as them is impossible. A custom or higher role that also grants
 *                  the data action would look like a miss here, so the copy says so.
 *  - "unavailable": neither was possible.
 */
export type AccessEvidence = "probe" | "assignment" | "unavailable";

export interface AccessProbe {
  evidence: AccessEvidence;
  /** probe: the HTTP status. assignment: ignored. */
  status?: number;
  /** assignment: whether a known data-plane role was found. */
  granted?: boolean;
  /** Resource the access is scoped to — named in every message. */
  scope: string | null;
  /** Object id we evaluated, for the remedy line. */
  principalId: string | null;
  /** True when the principal is the caller (drives "you" vs "they" and whether --for is echoed). */
  isSelf: boolean;
}

function grantRemedy(slug: "foundry-data-plane" | "keyvault-secrets", p: AccessProbe): string {
  const target = p.isSelf ? "m8t prereqs --fix" : `m8t prereqs --fix --for ${p.principalId ?? "<user>"}`;
  const role = slug === "foundry-data-plane" ? "Foundry User" : "Key Vault Secrets User";
  const scope = p.scope ?? `<${slug === "foundry-data-plane" ? "foundry-account" : "key-vault"}-resource-id>`;
  return (
    `${target}\n` +
    `        or by hand: az role assignment create --assignee-object-id ${p.principalId ?? "<object-id>"} ` +
    `--assignee-principal-type User --role "${role}" --scope ${scope}`
  );
}

export function evaluateFoundryAccess(p: AccessProbe): PrereqResult {
  const who = p.isSelf ? "your account" : "that account";
  if (p.evidence === "unavailable") {
    return result("foundry-data-plane", "skipped", `could not determine whether ${who} can reach the Foundry data plane`);
  }
  if (p.evidence === "probe") {
    if (p.status !== undefined && p.status >= 200 && p.status < 300) {
      return result("foundry-data-plane", "pass", `${who} can reach the Foundry data plane (HTTP ${p.status.toString()})`);
    }
    if (p.status === 401 || p.status === 403) {
      return result(
        "foundry-data-plane",
        "fail",
        `${who} is missing a Foundry data-plane role on ${p.scope ?? "the Foundry account"} (HTTP ${p.status.toString()}). ` +
          `Subscription Owner does not cover this — reading and invoking agents are data actions, and control-plane roles carry none.`,
        grantRemedy("foundry-data-plane", p),
      );
    }
    return result("foundry-data-plane", "warn", `unexpected response from the Foundry data plane (HTTP ${(p.status ?? 0).toString()})`);
  }
  // assignment evidence
  if (p.granted === true) {
    return result("foundry-data-plane", "pass", `${who} holds a Foundry data-plane role on ${p.scope ?? "the Foundry account"}`);
  }
  return result(
    "foundry-data-plane",
    "fail",
    `${who} holds no known Foundry data-plane role on ${p.scope ?? "the Foundry account"}. ` +
      `(Checked by role assignment, not by calling as them — a custom role granting the same data action would not be recognised here.)`,
    grantRemedy("foundry-data-plane", p),
  );
}

export function evaluateKeyVaultAccess(p: AccessProbe): PrereqResult {
  const who = p.isSelf ? "your account" : "that account";
  if (p.evidence === "unavailable") {
    return result("keyvault-secrets", "skipped", `could not determine whether ${who} can read the platform Key Vault`);
  }
  if (p.evidence === "probe") {
    if (p.status !== undefined && p.status >= 200 && p.status < 300) {
      return result("keyvault-secrets", "pass", `${who} can read secrets from ${p.scope ?? "the platform Key Vault"}`);
    }
    if (p.status === 401 || p.status === 403) {
      return result(
        "keyvault-secrets",
        "fail",
        `${who} cannot read secrets from ${p.scope ?? "the platform Key Vault"} (HTTP ${p.status.toString()}). ` +
          `Deploying a worker will fail — this is a permanent permission gap, not a transient blip.`,
        grantRemedy("keyvault-secrets", p),
      );
    }
    return result("keyvault-secrets", "warn", `unexpected response from the platform Key Vault (HTTP ${(p.status ?? 0).toString()})`);
  }
  if (p.granted === true) {
    return result("keyvault-secrets", "pass", `${who} holds Key Vault Secrets User on ${p.scope ?? "the platform Key Vault"}`);
  }
  return result(
    "keyvault-secrets",
    "fail",
    `${who} holds no secrets-read role on ${p.scope ?? "the platform Key Vault"}. ` +
      `(Checked by role assignment, not by calling as them.)`,
    grantRemedy("keyvault-secrets", p),
  );
}

export interface RedirectUriProbe {
  /** null when the app registration could not be read (common for non-admins). */
  registeredUris: readonly string[] | null;
  gatewayUrl: string | null;
  clientId: string | null;
}

/**
 * The #376 class: a gateway that answers HTTP 200 and refuses every sign-in.
 * Non-admins cannot read the app registration at all, so an unreadable app is
 * "skipped" — never a refusal, because the founder could not act on it anyway.
 */
export function evaluateRedirectUri(p: RedirectUriProbe): PrereqResult {
  if (!p.gatewayUrl) return result("signin-redirect-uri", "skipped", "no gateway URL resolved");
  if (p.registeredUris === null) {
    return result("signin-redirect-uri", "skipped", `could not read app registration ${p.clientId ?? "(unknown)"} — needs directory rights`);
  }
  const want = p.gatewayUrl.replace(/\/$/, "");
  if (p.registeredUris.some((u) => u.replace(/\/$/, "") === want)) {
    return result("signin-redirect-uri", "pass", `${want} is registered on the sign-in app`);
  }
  return result(
    "signin-redirect-uri",
    "fail",
    `${want} is not a registered redirect URI — sign-in will fail with AADSTS50011`,
    // Deliberately NOT "m8t prereqs --fix": this command reports it and does not
    // write it. The app registration is shared across every deployment in the
    // tenant, so the write belongs to the install, not to whoever is diagnosing.
    `Re-run 'm8t bootstrap status --finalize' (it registers this), or ask a directory admin to add it — MERGE, never overwrite:\n` +
      `        az ad app update --id ${p.clientId ?? "<clientId>"} --set spa.redirectUris="['${want}']"`,
  );
}

export function evaluatePlatformDiscoverable(p: { gatewayUrl: string | null; detail: string }): PrereqResult {
  if (p.gatewayUrl) return result("platform-discoverable", "pass", p.gatewayUrl);
  return result(
    "platform-discoverable",
    "fail",
    p.detail,
    "m8t switch   (point this machine at the right tenant/subscription), or pass --resource-group if the subscription holds several deployments",
  );
}

// ─── Verdict ────────────────────────────────────────────────────────────────

export interface PrereqVerdict {
  phase: PrereqPhase;
  ok: boolean;
  results: PrereqResult[];
  /** Rows in this phase that nothing checks — surfaced so the verdict never implies coverage it lacks. */
  unchecked: string[];
}

export function summarize(phase: PrereqPhase, results: PrereqResult[]): PrereqVerdict {
  return {
    phase,
    ok: !results.some((r) => r.status === "fail"),
    results,
    unchecked: prereqsForPhase(phase)
      .filter((p) => p.checkedBy === "none")
      .map((p) => p.slug),
  };
}

// ─── Docs rendering ─────────────────────────────────────────────────────────
//
// guides/prerequisites.md embeds these tables between sentinels, and a
// doc-drift test asserts the file matches this output byte for byte. Adding a
// prerequisite here and forgetting the guide is therefore a failing test rather
// than a silently stale document.

const COVERAGE_LABEL: Record<PrereqCoverage, string> = {
  auto: "Handled for you",
  refuses: "Checked — blocks until fixed",
  warns: "Checked — warns only",
  unhandled: "**Not checked** — you may still hit it",
};

const CHECKED_BY_LABEL: Record<PrereqCheckedBy, string> = {
  prereqs: "`m8t prereqs`",
  doctor: "`m8t doctor`",
  runbook: "the install runbook",
  none: "—",
};

export function renderPrereqTable(phase: PrereqPhase): string {
  const rows = prereqsForPhase(phase).map(
    (p) => `| ${p.title} | ${p.breaks} | ${COVERAGE_LABEL[p.coverage]} | ${CHECKED_BY_LABEL[p.checkedBy]} |`,
  );
  return ["| What | Why it matters | Status | Checked by |", "|---|---|---|---|", ...rows].join("\n");
}
